export interface KeyItem {
  id: string | null;
  code: string;
  name: string;
  referenceFactId?: string;
  keyType: 'text' | 'photo' | 'multiple' | string;
  exportAutoSize?: boolean;
}

export type ElementType = 'key' | 'heading' | 'text' | 'divider';

export interface CanvasElement {
  id: string;
  type: ElementType;
  colSpan: number; // 1 to 6
  forceNewRow?: boolean; // Forces item to start on a new row below
  enableIf?: boolean; // Toggle for {{#if}} condition (default true)
  // Key properties
  keyId?: string;
  keyCode?: string;
  keyName?: string;
  keyType?: 'text' | 'photo' | 'multiple' | string;
  showLabel?: boolean;
  customLabel?: string;
  // Photo properties
  photoHeight?: string; // 'h-32' | 'h-48' | 'h-64' | 'h-80' | 'h-96'
  photoFit?: 'object-cover' | 'object-contain' | 'object-fill';
  photoRounded?: 'rounded-lg' | 'rounded-xl' | 'rounded-2xl' | 'rounded-none';
  // Multiple properties
  galleryCols?: number; // 2 | 3 | 4
  // Heading / Text properties
  content?: string;
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4';
  uppercase?: boolean;
  textSize?: string;
  fontWeight?: string;
  textColor?: string;
}

export interface Section {
  id: string;
  title?: string;
  cols: number; // Fixed to 6
  gap: string; // 'gap-2' | 'gap-4' | 'gap-6' | 'gap-8'
  marginBottom: string; // 'mb-2' | 'mb-4' | 'mb-6' | 'mb-8'
  showDivider?: boolean;
  items: CanvasElement[];
}

/**
 * Serializes sections array into formatted Tailwind HTML markup.
 */
export const serializeSectionsToHtml = (sections: Section[]): string => {
  if (!sections || sections.length === 0) return '';

  const htmlChunks: string[] = [];

  htmlChunks.push('<div class="space-y-8 font-sans">');

  sections.forEach((sec) => {
    if (sec.showDivider) {
      htmlChunks.push('  <hr class="border-gray-100 my-6" />');
    }

    htmlChunks.push('  <div>');

    if (sec.title && sec.title.trim()) {
      htmlChunks.push(
        `    <h2 class="text-xl font-bold text-gray-900 uppercase tracking-widest mb-6">${sec.title.trim()}</h2>`
      );
    }

    const secGap = sec.gap || 'gap-6';
    const secMb = sec.marginBottom || 'mb-4';

    htmlChunks.push(
      `    <div class="grid grid-cols-6 ${secGap} ${secMb}" style="display: grid; grid-template-columns: repeat(6, minmax(0, 1fr));">`
    );

    sec.items.forEach((item) => {
      if (item.forceNewRow) {
        htmlChunks.push(
          '      <div class="col-span-full w-full" style="grid-column: 1 / -1;"></div>'
        );
      }

      const spanClass = getItemColSpanClass(item.colSpan);
      const spanStyle = getItemColSpanStyle(item.colSpan);
      const spanAttr = ` class="${spanClass}" style="${spanStyle}"`;

      if (item.type === 'key') {
        const typeNormalized = (item.keyType || 'text').toLowerCase();
        const code = item.keyCode || 'KEY_CODE';
        const labelExpr = item.customLabel || `\${${code}_LABEL}`;
        const valueExpr = `\${${code}_VALUE}`;
        const showLabel = item.showLabel !== false;
        const isIf = Boolean(item.enableIf); // read as-is (false if not explicitly enabled)

        if (isIf) {
          htmlChunks.push(`      {{#if ${code}_VALUE}}`);
        }

        if (typeNormalized === 'photo') {
          const height = item.photoHeight || 'h-64';
          const fit = item.photoFit || 'object-cover';
          const rounded = item.photoRounded || 'rounded-xl';

          htmlChunks.push(`      <div${spanAttr}>`);
          if (showLabel) {
            htmlChunks.push(`        <p class="text-xs text-gray-500 mb-2 uppercase tracking-tight">`);
            htmlChunks.push(`          ${labelExpr}`);
            htmlChunks.push(`        </p>`);
          }
          htmlChunks.push(`        <a href="${valueExpr}" target="_blank" rel="noopener noreferrer">`);
          htmlChunks.push(
            `          <img src="${valueExpr}" class="w-full ${height} ${fit} ${rounded} shadow-sm cursor-pointer hover:opacity-90 hover:shadow-md transition duration-200" />`
          );
          htmlChunks.push(`        </a>`);
          htmlChunks.push(`      </div>`);
        } else if (typeNormalized === 'multiple') {
          htmlChunks.push(`      <div${spanAttr}>`);
          if (showLabel) {
            htmlChunks.push(`        <p class="text-xs text-gray-500 mb-2 uppercase tracking-tight">`);
            htmlChunks.push(`          ${labelExpr}`);
            htmlChunks.push(`        </p>`);
          }
          htmlChunks.push(`        <div class="flex flex-wrap gap-2">`);
          htmlChunks.push(`          {{#each ${valueExpr}}}`);
          htmlChunks.push(
            `          <span class="inline-block bg-blue-600 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm">`
          );
          htmlChunks.push(`            {{this}}`);
          htmlChunks.push(`          </span>`);
          htmlChunks.push(`          {{/each}}`);
          htmlChunks.push(`        </div>`);
          htmlChunks.push(`      </div>`);
        } else {
          // Text key
          const textSize = item.textSize || 'font-semibold text-gray-800';
          htmlChunks.push(`      <div${spanAttr}>`);
          if (showLabel) {
            htmlChunks.push(`        <p class="text-xs text-gray-500 mb-1 uppercase tracking-tight">`);
            htmlChunks.push(`          ${labelExpr}`);
            htmlChunks.push(`        </p>`);
          }
          htmlChunks.push(`        <p class="${textSize}">`);
          htmlChunks.push(`          ${valueExpr}`);
          htmlChunks.push(`        </p>`);
          htmlChunks.push(`      </div>`);
        }

        if (isIf) {
          htmlChunks.push(`      {{/if}}`);
        }
      } else if (item.type === 'heading') {
        const level = item.headingLevel || 'h3';
        const isUpper = item.uppercase !== false ? 'uppercase tracking-wider' : '';
        const weight = item.fontWeight || 'font-bold';
        const color = item.textColor || 'text-gray-900';
        const size = item.textSize || 'text-lg';

        htmlChunks.push(`      <div${spanAttr}>`);
        htmlChunks.push(
          `        <${level} class="${size} ${weight} ${color} ${isUpper} mb-2">${item.content || 'Heading'
          }</${level}>`
        );
        htmlChunks.push(`      </div>`);
      } else if (item.type === 'text') {
        const weight = item.fontWeight || 'font-normal';
        const color = item.textColor || 'text-gray-700';
        const size = item.textSize || 'text-sm';

        htmlChunks.push(`      <div${spanAttr}>`);
        htmlChunks.push(`        <p class="${size} ${weight} ${color}">${item.content || 'Text content'}</p>`);
        htmlChunks.push(`      </div>`);
      } else if (item.type === 'divider') {
        htmlChunks.push(`      <div${spanAttr}>`);
        htmlChunks.push(`        <hr class="border-gray-200 my-4" />`);
        htmlChunks.push(`      </div>`);
      }
    });

    htmlChunks.push('    </div>');
    htmlChunks.push('  </div>');
  });

  htmlChunks.push('</div>');

  return htmlChunks.join('\n');
};

export const getItemColSpanClass = (span: number): string => {
  const safeSpan = Math.max(1, Math.min(6, span));
  if (safeSpan === 6) return 'col-span-full';
  return `col-span-${safeSpan} md:col-span-${safeSpan}`;
};

export const getItemColSpanStyle = (span: number): string => {
  const safeSpan = Math.max(1, Math.min(6, span));
  if (safeSpan === 6) return 'grid-column: 1 / -1;';
  return `grid-column: span ${safeSpan} / span ${safeSpan};`;
};

/**
 * Parses an HTML template string back into structured Section[] for Visual Canvas.
 */
export const parseHtmlToSections = (
  html: string,
  availableKeys: KeyItem[] = []
): Section[] => {
  if (!html || !html.trim()) return [];

  let doc: Document;
  try {
    const parser = new DOMParser();
    doc = parser.parseFromString(html, 'text/html');
  } catch (err) {
    console.error('Failed to parse template HTML:', err);
    return [];
  }

  const sections: Section[] = [];
  let gridElements = Array.from(doc.body.querySelectorAll('.grid')) as HTMLElement[];

  // Fallback: If no element has class "grid", check elements with style display:grid or grid-template-columns
  if (gridElements.length === 0) {
    const allDivs = Array.from(doc.body.querySelectorAll('div')) as HTMLElement[];
    gridElements = allDivs.filter((d) => {
      const st = d.getAttribute('style') || '';
      return st.includes('grid-template-columns') || st.includes('display: grid');
    });
  }

  gridElements.forEach((gridEl, secIdx) => {
    // Determine section title
    let title = `SECTION ${secIdx + 1}`;
    const parent = gridEl.parentElement;

    if (parent) {
      const headingInParent = parent.querySelector('h1, h2, h3, h4');
      if (headingInParent && !gridEl.contains(headingInParent)) {
        title = headingInParent.textContent?.trim() || title;
      } else {
        let prev = gridEl.previousElementSibling;
        while (prev) {
          if (['H1', 'H2', 'H3', 'H4'].includes(prev.tagName)) {
            title = prev.textContent?.trim() || title;
            break;
          }
          prev = prev.previousElementSibling;
        }
      }
    }

    // Determine divider: check preceding sibling of parent or of gridEl
    let showDivider = false;
    const prevEl = parent && parent !== doc.body ? parent.previousElementSibling : gridEl.previousElementSibling;
    if (prevEl && prevEl.tagName === 'HR') {
      showDivider = true;
    }

    // Extract gap and margin
    const gridClass = gridEl.getAttribute('class') || '';
    const gapMatch = gridClass.match(/\b(gap-[0-9]+)\b/);
    const mbMatch = gridClass.match(/\b(mb-[0-9]+)\b/);
    const gap = gapMatch ? gapMatch[1] : 'gap-6';
    const marginBottom = mbMatch ? mbMatch[1] : 'mb-4';

    // Parse items inside grid
    const items: CanvasElement[] = [];
    let forceNextNewRow = false;
    let pendingIf = false;

    const gridNodes = Array.from(gridEl.childNodes);

    gridNodes.forEach((node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        const text = node.textContent || '';
        if (text.includes('{{#if')) {
          pendingIf = true;
        } else if (text.includes('{{/if}}')) {
          pendingIf = false;
        }
        return;
      }

      if (node.nodeType !== Node.ELEMENT_NODE) return;

      const elem = node as HTMLElement;
      const classAttr = elem.getAttribute('class') || '';
      const styleAttr = elem.getAttribute('style') || '';

      // Check if this is a force-new-row spacer div
      if (
        (classAttr.includes('col-span-full') || styleAttr.includes('1 / -1')) &&
        classAttr.includes('w-full') &&
        elem.children.length === 0 &&
        !elem.textContent?.trim()
      ) {
        forceNextNewRow = true;
        return;
      }

      // Calculate colSpan from classes or style
      let colSpan = 3;
      if (
        classAttr.includes('col-span-full') ||
        classAttr.includes('col-span-6') ||
        classAttr.includes('md:col-span-6') ||
        styleAttr.includes('1 / -1') ||
        styleAttr.includes('span 6')
      ) {
        colSpan = 6;
      } else if (
        classAttr.includes('col-span-5') ||
        classAttr.includes('md:col-span-5') ||
        styleAttr.includes('span 5')
      ) {
        colSpan = 5;
      } else if (
        classAttr.includes('col-span-4') ||
        classAttr.includes('md:col-span-4') ||
        styleAttr.includes('span 4')
      ) {
        colSpan = 4;
      } else if (
        classAttr.includes('col-span-3') ||
        classAttr.includes('md:col-span-3') ||
        styleAttr.includes('span 3')
      ) {
        colSpan = 3;
      } else if (
        classAttr.includes('col-span-2') ||
        classAttr.includes('md:col-span-2') ||
        styleAttr.includes('span 2')
      ) {
        colSpan = 2;
      } else if (
        classAttr.includes('col-span-1') ||
        classAttr.includes('md:col-span-1') ||
        styleAttr.includes('span 1')
      ) {
        colSpan = 1;
      }

      // Check for {{#if}} wrapping
      const enableIf =
        pendingIf || elem.innerHTML.includes('{{#if') || elem.outerHTML.includes('{{#if');
      pendingIf = false; // consume

      const innerHtml = elem.innerHTML;

      // Try matching Key: ${CODE_VALUE} or ${CODE_LABEL} or ${CODE}
      const keyMatch =
        innerHtml.match(/\$\{([A-Za-z0-9_]+)_(VALUE|LABEL)\}/) ||
        innerHtml.match(/\$\{([A-Za-z0-9_]+)\}/);

      if (keyMatch) {
        const keyCode = keyMatch[1];
        const matchedKey = availableKeys.find(
          (k) => k.code && k.code.toUpperCase() === keyCode.toUpperCase()
        );

        const imgEl = elem.querySelector('img');
        const isMultiple =
          innerHtml.includes('{{#each') ||
          (matchedKey?.keyType || '').toLowerCase() === 'multiple';
        const isPhoto = !!imgEl || (matchedKey?.keyType || '').toLowerCase() === 'photo';

        const keyType = isPhoto ? 'photo' : isMultiple ? 'multiple' : (matchedKey?.keyType || 'text');
        const keyName = matchedKey?.name || keyCode.replace(/_/g, ' ');

        // Detect label
        const labelP = elem.querySelector('p.text-gray-500, p.uppercase');
        const showLabel = !!labelP;
        let customLabel: string | undefined = undefined;
        if (labelP) {
          const rawLabelText = labelP.textContent?.trim() || '';
          if (rawLabelText && !rawLabelText.includes(`\${${keyCode}_LABEL}`)) {
            customLabel = rawLabelText;
          }
        }

        // Photo properties
        let photoHeight = 'h-64';
        let photoFit: 'object-cover' | 'object-contain' | 'object-fill' = 'object-cover';
        let photoRounded: 'rounded-lg' | 'rounded-xl' | 'rounded-2xl' | 'rounded-none' = 'rounded-xl';

        if (imgEl) {
          const imgClasses = imgEl.getAttribute('class') || '';
          const hMatch = imgClasses.match(/\b(h-(?:32|48|64|80|96))\b/);
          if (hMatch) photoHeight = hMatch[1];

          if (imgClasses.includes('object-contain')) photoFit = 'object-contain';
          else if (imgClasses.includes('object-fill')) photoFit = 'object-fill';
          else photoFit = 'object-cover';

          if (imgClasses.includes('rounded-2xl')) photoRounded = 'rounded-2xl';
          else if (imgClasses.includes('rounded-lg')) photoRounded = 'rounded-lg';
          else if (imgClasses.includes('rounded-none')) photoRounded = 'rounded-none';
          else photoRounded = 'rounded-xl';
        }

        // Text properties
        let textSize = 'font-semibold text-gray-800';
        if (keyType === 'text') {
          const pElements = elem.querySelectorAll('p');
          if (pElements.length > 1) {
            textSize = pElements[1].getAttribute('class') || textSize;
          } else if (pElements.length === 1 && !showLabel) {
            textSize = pElements[0].getAttribute('class') || textSize;
          }
        }

        items.push({
          id: `elem-${Date.now()}-${items.length}-${Math.random().toString(36).substring(2, 6)}`,
          type: 'key',
          colSpan,
          forceNewRow: forceNextNewRow,
          enableIf,
          keyId: matchedKey?.id || undefined,
          keyCode,
          keyName,
          keyType,
          showLabel,
          customLabel,
          photoHeight: isPhoto ? photoHeight : undefined,
          photoFit: isPhoto ? photoFit : undefined,
          photoRounded: isPhoto ? photoRounded : undefined,
          galleryCols: 3,
          textSize: keyType === 'text' ? textSize : undefined,
        });

        forceNextNewRow = false;
        return;
      }

      // Check for Divider
      if (elem.querySelector('hr')) {
        items.push({
          id: `elem-${Date.now()}-${items.length}-${Math.random().toString(36).substring(2, 6)}`,
          type: 'divider',
          colSpan,
          forceNewRow: forceNextNewRow,
        });
        forceNextNewRow = false;
        return;
      }

      // Check for Heading
      const headingInItem = elem.querySelector('h1, h2, h3, h4');
      if (headingInItem) {
        const level = headingInItem.tagName.toLowerCase() as 'h1' | 'h2' | 'h3' | 'h4';
        const headingClass = headingInItem.getAttribute('class') || '';
        items.push({
          id: `elem-${Date.now()}-${items.length}-${Math.random().toString(36).substring(2, 6)}`,
          type: 'heading',
          colSpan,
          forceNewRow: forceNextNewRow,
          headingLevel: level,
          content: headingInItem.textContent?.trim() || 'Heading',
          uppercase: headingClass.includes('uppercase'),
        });
        forceNextNewRow = false;
        return;
      }

      // Check for Custom Text
      const pInItem = elem.querySelector('p');
      if (pInItem) {
        items.push({
          id: `elem-${Date.now()}-${items.length}-${Math.random().toString(36).substring(2, 6)}`,
          type: 'text',
          colSpan,
          forceNewRow: forceNextNewRow,
          content: pInItem.textContent?.trim() || 'Text content',
        });
        forceNextNewRow = false;
        return;
      }
    });

    sections.push({
      id: `sec-${Date.now()}-${sections.length}-${Math.random().toString(36).substring(2, 6)}`,
      title,
      cols: 6,
      gap,
      marginBottom,
      showDivider,
      items,
    });
  });

  return sections;
};

/**
 * Generates default sample sections for quick starting with fixed 6-grid system.
 */
export const getDefaultSampleSections = (availableKeys: KeyItem[]): Section[] => {
  const textKeys = availableKeys.filter((k) => (k.keyType || '').toLowerCase() === 'text');
  const photoKeys = availableKeys.filter((k) => (k.keyType || '').toLowerCase() === 'photo');
  const multiKeys = availableKeys.filter((k) => (k.keyType || '').toLowerCase() === 'multiple');

  const sec1Items: CanvasElement[] = [];

  // Main storefront photo (spans 3 of 6 cols -> 50%)
  if (photoKeys.length > 0) {
    const mainPhoto = photoKeys[0];
    sec1Items.push({
      id: `elem-${Date.now()}-1`,
      type: 'key',
      colSpan: 3,
      keyId: mainPhoto.id || undefined,
      keyCode: mainPhoto.code,
      keyName: mainPhoto.name,
      keyType: mainPhoto.keyType,
      showLabel: true,
      photoHeight: 'h-64',
      photoFit: 'object-cover',
      photoRounded: 'rounded-xl',
    });
  }

  // Text keys
  textKeys.slice(0, 4).forEach((tk, idx) => {
    sec1Items.push({
      id: `elem-${Date.now()}-${idx + 2}`,
      type: 'key',
      colSpan: 3,
      keyId: tk.id || undefined,
      keyCode: tk.code,
      keyName: tk.name,
      keyType: tk.keyType,
      showLabel: true,
    });
  });

  const sections: Section[] = [
    {
      id: `sec-${Date.now()}-1`,
      title: 'STORE PROFILE',
      cols: 6,
      gap: 'gap-6',
      marginBottom: 'mb-4',
      showDivider: false,
      items: sec1Items,
    },
  ];

  if (textKeys.length > 4) {
    const sec2Items: CanvasElement[] = textKeys.slice(4, 10).map((tk, idx) => ({
      id: `elem-sec2-${Date.now()}-${idx}`,
      type: 'key',
      colSpan: 3,
      keyId: tk.id || undefined,
      keyCode: tk.code,
      keyName: tk.name,
      keyType: tk.keyType,
      showLabel: true,
    }));

    sections.push({
      id: `sec-${Date.now()}-2`,
      title: 'ADDITIONAL INFORMATION',
      cols: 6,
      gap: 'gap-6',
      marginBottom: 'mb-4',
      showDivider: true,
      items: sec2Items,
    });
  }

  if (photoKeys.length > 1 || multiKeys.length > 0) {
    const photoSecItems: CanvasElement[] = [];

    photoKeys.slice(1).forEach((pk, idx) => {
      photoSecItems.push({
        id: `elem-photo-${Date.now()}-${idx}`,
        type: 'key',
        colSpan: 3,
        keyId: pk.id || undefined,
        keyCode: pk.code,
        keyName: pk.name,
        keyType: pk.keyType,
        showLabel: true,
        photoHeight: 'h-64',
        photoFit: 'object-cover',
        photoRounded: 'rounded-xl',
      });
    });

    multiKeys.forEach((mk, idx) => {
      photoSecItems.push({
        id: `elem-multi-${Date.now()}-${idx}`,
        type: 'key',
        colSpan: 6,
        keyId: mk.id || undefined,
        keyCode: mk.code,
        keyName: mk.name,
        keyType: mk.keyType,
        showLabel: true,
        galleryCols: 3,
      });
    });

    sections.push({
      id: `sec-${Date.now()}-3`,
      title: 'EVIDENCE & PHOTOS',
      cols: 6,
      gap: 'gap-6',
      marginBottom: 'mb-4',
      showDivider: true,
      items: photoSecItems,
    });
  }

  return sections;
};
